import isEmpty from "lodash/isEmpty";

import { checkIsNotNullish } from "@tsp-core/utils/nullish";

import { Exception } from "../index";

export function getExceptionCode(exception: unknown, fallback: number | string = 0): number | string {
  if (exception instanceof Error) {
    if ("code" in exception) {
      if (typeof exception.code === "string") {
        const code = parseInt(exception.code, 10);
        if (!Number.isNaN(code)) {
          return code;
        }

        return exception.code;
      }

      if (typeof exception.code === "number") {
        if (!Number.isNaN(exception.code)) {
          return exception.code;
        }

        return fallback;
      }
    }
  }

  if (typeof exception === "object" && checkIsNotNullish(exception)) {
    if ("code" in exception) {
      if (typeof exception.code === "number") {
        return exception.code;
      }
    }
  }

  if (typeof exception === "object" && checkIsNotNullish(exception)) {
    if ("status" in exception) {
      if (typeof exception.status === "number") {
        return exception.status;
      }
    }
  }

  return fallback;
}

export function getExceptionMessage(exception: unknown, fallback = "Unknown error"): string {
  if (exception instanceof Error) {
    if ("message" in exception) {
      if (typeof exception.message === "string") {
        return exception.message;
      }
    }
  }

  if (typeof exception === "object" && checkIsNotNullish(exception)) {
    if ("message" in exception) {
      if (typeof exception.message === "string") {
        return exception.message;
      }
    }

    if ("data" in exception) {
      if (typeof exception.data === "object" && checkIsNotNullish(exception.data)) {
        if ("message" in exception.data) {
          if (typeof exception.data.message === "string") {
            return exception.data.message;
          }
        }

        if ("detail" in exception.data) {
          if (typeof exception.data.detail === "string") {
            return exception.data.detail;
          }
        }
      }
    }

    if ("status" in exception) {
      if (typeof exception.status === "string") {
        return exception.status;
      }
    }

    if ("error" in exception) {
      return getExceptionMessage(exception.error, fallback);
    }
  }

  return fallback;
}

export function getExceptionDetails<Details>(exception: unknown, fallback: Details): Details {
  if (exception instanceof Error) {
    if ("details" in exception) {
      return exception.details as Details;
    }
  }

  if (typeof exception === "object" && checkIsNotNullish(exception)) {
    if ("data" in exception) {
      if (typeof exception.data === "object" && checkIsNotNullish(exception.data)) {
        if ("validation_messages" in exception.data) {
          const { validation_messages } = exception.data;

          if (Array.isArray(validation_messages)) {
            return validation_messages as Details;
          }

          if (typeof validation_messages === "object" && checkIsNotNullish(validation_messages)) {
            const details: Record<string, string> = {};

            Object.entries(validation_messages).forEach(([key, validation_message]) => {
              let message = null;
              if (typeof validation_message === "string") {
                message = validation_message;
              }
              if (Array.isArray(validation_message)) {
                message = validation_message.join(", ");
              }
              if (typeof validation_message === "object" && checkIsNotNullish(validation_message)) {
                message = Object.values(validation_message).join(", ");
              }

              if (message) {
                details[key] = message;
              }
            });

            if (!isEmpty(details)) {
              return details as Details;
            }
          }
        }

        if ("payload" in exception.data) {
          if (typeof exception.data.payload === "object" && checkIsNotNullish(exception.data.payload)) {
            if ("errors" in exception.data.payload) {
              const errors = exception.data.payload.errors;
              if (typeof errors === "object" && checkIsNotNullish(errors)) {
                return errors as Details;
              }
            }
          }
        }

        if ("detail" in exception.data && checkIsNotNullish(exception.data.detail)) {
          if (typeof exception.data.detail === "object" && checkIsNotNullish(exception.data.detail)) {
            const errors: Record<string, string> = {};

            Object.entries(exception.data.detail).forEach(([key, input]) => {
              if (typeof input === "string") {
                errors[key] = input;
              }

              if (Array.isArray(input) && input.length > 0) {
                errors[key] = input.join(", ");
              }
            });

            if (!isEmpty(errors)) {
              return errors as Details;
            }
          }
        }
      }
    }
  }

  return fallback;
}

export function getExceptionFromUnknown<Details>(
  input: unknown,
  code: number | string = 0,
  message = "Unknown error",
  details: Details = null,
): Exception<Details> {
  return new Exception<Details>(
    getExceptionCode(input, code),
    getExceptionMessage(input, message),
    getExceptionDetails<Details>(input, details),
  );
}
